<template>
  <div class="ai-conversations container-fluid py-4">
    <div class="mb-3">
      <h2>AI Conversations</h2>
      <p class="text-muted mb-0">
        Every AI chat and document analysis, with the prompts and recorded
        output behind each turn.
      </p>
    </div>
    <AiConversationFiltersComponent
      v-model="filters"
      :conversationId="selectedId"
      @jump="jump"
    />
    <div v-if="!hasWorkspace">
      <p
        v-if="list.status === 'loading'"
        class="text-center py-5"
        role="status"
      >
        Loading conversations…
      </p>
      <div
        v-else-if="list.status === 'error'"
        class="alert alert-warning mt-3"
        role="alert"
      >
        Could not load conversations.
        <button class="btn btn-link" @click="retryList">Try again</button>
      </div>
      <AiConversationTableComponent
        v-else
        :rows="list.data.items"
        :hasMore="hasMore"
        :loadingMore="list.loadingMore"
        :moreFailed="list.moreFailed"
        @select="selectRow"
        @more="loadMore"
      />
    </div>
    <div v-else class="workspace mt-3">
      <AiConversationListComponent
        :rows="list.data.items"
        :total="list.data.total"
        :selectedId="selectedId"
        :mode="filters.mode"
        :hasMore="hasMore"
        :loading="
          list.status === 'loading' ||
          (list.status === 'idle' && trace.status === 'loading')
        "
        :ready="list.status === 'ready'"
        :failed="list.status === 'error'"
        :loadingMore="list.loadingMore"
        :moreFailed="list.moreFailed"
        @select="selectRow"
        @more="loadMore"
        @retry="retryList"
      />
      <div
        v-if="trace.status === 'loading'"
        class="trace-placeholder"
        role="status"
      >
        Loading trace…
      </div>
      <div
        v-else-if="trace.status === 'error'"
        class="trace-placeholder"
        role="alert"
      >
        <p>Could not load this conversation.</p>
        <button class="btn btn-outline-primary" @click="retryTrace">
          Try again
        </button>
      </div>
      <AiConversationTraceComponent v-else-if="detail" :conversation="detail" />
      <div v-else class="trace-placeholder">
        <h4>
          {{
            trace.data
              ? "Conversation outside this selection"
              : "Select a conversation"
          }}
        </h4>
        <p>Choose a conversation to inspect its turn-by-turn audit trace.</p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import type { ConversationQuery } from "./aiConversationsTypes";
import { useAiConversationQuery } from "./useAiConversationQuery";
import {
  useAiConversationDetail,
  useAiConversationList,
} from "./useAiConversationResources";
import AiConversationFiltersComponent from "./AiConversationFiltersComponent.vue";
import AiConversationTableComponent from "./AiConversationTableComponent.vue";
import AiConversationListComponent from "./AiConversationListComponent.vue";
import AiConversationTraceComponent from "./AiConversationTraceComponent.vue";

const {
  filters: routeFilters,
  selectedId,
  selectRow,
  jump,
} = useAiConversationQuery();
const { state: trace, retry: retryTrace } = useAiConversationDetail(selectedId);
const detail = computed(() => {
  const data = trace.value.data;
  if (!data || data.id !== selectedId.value) return null;
  if (
    routeFilters.value.companyId &&
    data.company.id !== routeFilters.value.companyId
  )
    return null;
  if (
    routeFilters.value.accountId &&
    data.account.id !== routeFilters.value.accountId
  )
    return null;
  return data;
});
const filters = computed({
  get: () => {
    const value = routeFilters.value;
    if (value.companyId || value.accountId || !detail.value) return value;
    return value.mode === "company"
      ? { ...value, companyId: detail.value.company.id }
      : { ...value, accountId: detail.value.account.id };
  },
  set: (value) => {
    routeFilters.value = value;
  },
});
const query = computed<ConversationQuery | null>(() => {
  const scoped = filters.value.companyId || filters.value.accountId;
  if (!scoped && selectedId.value && !detail.value) return null;
  return {
    company_id: filters.value.companyId ?? undefined,
    account_id: filters.value.accountId ?? undefined,
    conversation_types: filters.value.types,
    since: filters.value.since,
    until: filters.value.until,
    include_staff: filters.value.includeStaff,
    include_test_companies: filters.value.includeTest,
  };
});
const {
  state: list,
  loadMore,
  retry: retryList,
} = useAiConversationList(query);
const hasWorkspace = computed(() =>
  Boolean(
    filters.value.companyId || filters.value.accountId || selectedId.value
  )
);
const hasMore = computed(() => list.value.data.has_more);
</script>
<style scoped lang="scss">
.ai-conversations {
  font-family: "Nunito Sans", var(--phoenix-font-sans-serif);
  --phoenix-border-color: color-mix(
    in srgb,
    var(--phoenix-body-color) 15%,
    transparent
  );
}
h2 {
  font-family: inherit;
  font-size: 1.65rem;
  font-weight: 700;
}
:deep(h5),
:deep(h6) {
  font-family: inherit;
}

.workspace {
  display: grid;
  grid-template-columns: minmax(280px, 440px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}
.trace-placeholder {
  text-align: center;
  padding: 4rem 1.5rem;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 6px;
  background: var(--phoenix-body-bg);
  color: var(--phoenix-secondary-color);
}
@media (max-width: 1100px) {
  .workspace {
    grid-template-columns: 320px minmax(0, 1fr);
  }
}
@media (max-width: 800px) {
  .workspace {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
